import { links } from "@/constants";
import { TServerErrorResponse } from "@/types";
import { AxiosError } from "axios";
import {
  B2BMagicLinksDiscoveryAuthenticateResponse,
  B2BOAuthDiscoveryAuthenticateResponse,
  B2BSSOGetConnectionsResponse,
  Member,
  Organization,
} from "stytch";
import { authHttp } from "../httpHandler";

export const AUTH_ORGANIZATION_LOOKUP = "auth-organization-lookup";
export const AUTH_LOGIN = "auth-login";
export const AUTH_FORGOT_PASSWORD = "auth-forgot-password";
export const AUTH_RESET_PASSWORD = "auth-reset-password";
export const MFA_ENROLL_TOTP = "mfa-enroll-totp";
export const MFA_VERIFY_TOTP = "mfa-verify-totp";
export const AUTH_RECOVERY_CODES = "AUTH_RECOVERY_CODES";
export const AUTH_RECOVERY_CODES_AUTHENTICATE =
  "AUTH_RECOVERY_CODES_AUTHENTICATE";
export const AUTH_SESSION_DATA = "auth-session-data";
export const DISCOVERY_MAGIC_LINK = "discovery-magic-link";
export const SSO_GET_CONNECTIONS = "sso-get-connections";
export const EXCHANGE_SESSION_TOKEN = "exchange-session-token";
export const OAUTH_CALLBACK = "oauth-callback";
export const SSO_CALLBACK = "sso-callback";

export interface MFARequiredResponse {
  member_options: {
    mfa_phone_number: string;
    totp_registration_id: string;
  };
  secondary_auth_initiated: string;
}

export interface LoginResponse {
  intermediate_session_token: string;
  message: string;
  member_id: string;
  organization_id: string;
  mfa_enrolled: boolean;
  mfa_required: MFARequiredResponse;
  organization_mfa_policy: string;
  organization: Organization;
}

export interface MFAEnrollTOTPResponse {
  message: string;
  secret: string;
  qr_code_url: string;
}

// Recovery code API types and functions
interface RecoveryCodesRequest {
  member_id: string;
  organization_id: string;
  action?: "get" | "rotate";
}

interface RecoveryCodesResponse {
  message: string;
  recovery_codes: string[];
}

interface RecoveryCodeAuthenticateRequest {
  recovery_code: string;
  member_id: string;
  organization_id: string;
  intermediate_session_token?: string;
}

interface RecoveryCodeAuthenticateResponse extends LoginResponse {
  recovery_codes_remaining?: number;
}

export const login = async (data: {
  email: string;
  password: string;
  organization_id: string;
}) => {
  try {
    const response = await authHttp.post<LoginResponse>(links.auth.login, data);
    return response.data;
  } catch (error) {
    throw error as TServerErrorResponse;
  }
};

export const exchangeToken = async (data: {
  intermediate_session_token?: string;
  organization_id: string;
  session_token?: string;
}) => {
  try {
    const response = await authHttp.post<LoginResponse>(
      links.auth.sessionExchange,
      data,
    );
    return response.data;
  } catch (error) {
    throw error as TServerErrorResponse;
  }
};

export const organizationLookup = async (email: string) => {
  try {
    const response = await authHttp.post<{
      organizations: Organization[];
    }>(links.auth.organizationLookup, { email });
    return response.data;
  } catch (error) {
    throw error as TServerErrorResponse;
  }
};

export const forgotPassword = async ({
  email,
  organization_id,
}: {
  email: string;
  organization_id: string;
}) => {
  try {
    const response = await authHttp.post<void>(links.auth.forgotPassword, {
      email,
      organization_id,
    });

    return response.data;
  } catch (error) {
    throw error as TServerErrorResponse;
  }
};

export const resetPassword = async ({
  password,
  token,
  type = "email",
}: {
  password: string;
  token: string;
  type: "email" | "discovery";
}) => {
  try {
    const resetLink =
      type === "email"
        ? links.auth.resetPassword
        : links.auth.resetPasswordDiscovery;
    const response = await authHttp.post<void>(resetLink, {
      password,
      token,
    });
    return response.data;
  } catch (error) {
    throw error as TServerErrorResponse;
  }
};

export const validatePasswordStrength = async (password: string) => {
  try {
    const response = await authHttp.post<{ message: string }>(
      links.auth.passwordStrengthCheck,
      {
        password,
      },
    );
    return response.data;
  } catch (error) {
    throw error as TServerErrorResponse;
  }
};

export const mfaTotpEnroll = async ({
  member_id,
  organization_id,
}: {
  member_id: string;
  organization_id: string;
}) => {
  try {
    const response = await authHttp.post<MFAEnrollTOTPResponse>(
      links.auth.mfaTotpEnroll,
      {
        member_id,
        organization_id,
      },
    );
    return response.data;
  } catch (error) {
    throw error as AxiosError;
  }
};

export const mfaTotpVerify = async ({
  code,
  organization_id,
  member_id,
  intermediate_session_token,
}: {
  code: string;
  organization_id: string;
  member_id: string;
  intermediate_session_token: string;
}) => {
  try {
    const response = await authHttp.post<void>(links.auth.mfaTotpVerify, {
      code,
      member_id,
      organization_id,
      intermediate_session_token,
    });
    return response.data;
  } catch (error) {
    throw error as TServerErrorResponse;
  }
};

export interface SSOConnection {
  connection_id: string;
  organization_id: string;
  status: string;
  connection_type: "saml" | "oidc";
  display_name?: string;
  identity_provider?: string;
}

export interface SSOGetConnectionsResponse {
  saml_connections?: SSOConnection[];
  oidc_connections?: SSOConnection[];
  public_token: string;
  organization_id: string;
}

export interface SSOLoginCallbackResponse {
  message?: string;
  member_id?: string;
  organization_id?: string;
  user?: Member;
  organizations?: Organization[];
  session_token?: string;
  email_address?: string;
}

export const ssoLoginCallback = async ({
  token,
  stytch_token_type,
}: {
  token: string;
  stytch_token_type: string;
}): Promise<SSOLoginCallbackResponse> => {
  try {
    const response = await authHttp.get<SSOLoginCallbackResponse>(
      links.auth.ssoCallback,
      {
        params: {
          token,
          stytch_token_type,
        },
      },
    );

    return response.data;
  } catch (error) {
    throw error as TServerErrorResponse;
  }
};

interface SsOGetConnectionsParams {
  public_token: string;
  using_parent_connection?: boolean;
  parent_organization_id?: string;
  child_organization_id?: string;
}

export const ssoGetConnections = async ({
  organization_id,
}: {
  organization_id: string;
}): Promise<B2BSSOGetConnectionsResponse & SsOGetConnectionsParams> => {
  try {
    const response = await authHttp.get<
      B2BSSOGetConnectionsResponse & SsOGetConnectionsParams
    >(links.auth.ssoConnection, {
      params: { organization_id },
    });
    return response.data;
  } catch (error) {
    throw error as TServerErrorResponse;
  }
};

export const oauthLoginCallback = async ({
  token,
  stytch_token_type,
}: {
  token: string;
  stytch_token_type: string;
}): Promise<B2BOAuthDiscoveryAuthenticateResponse> => {
  try {
    const response = await authHttp<
      { token: string; stytch_token_type: string },
      { data: { message: string; data: B2BOAuthDiscoveryAuthenticateResponse } }
    >(links.auth.oauthCallback, {
      params: {
        token,
        stytch_token_type,
      },
    });
    return response.data.data;
  } catch (error) {
    console.log("error", error);
    throw error as TServerErrorResponse;
  }
};

export const sessionAuthenticate = async ({
  organization_id,
  member_id,
  intermediate_session_token,
}: {
  organization_id: string;
  member_id: string;
  intermediate_session_token: string;
}): Promise<B2BOAuthDiscoveryAuthenticateResponse> => {
  try {
    const response = await authHttp.post<
      {
        organization_id: string;
        member_id: string;
        intermediate_session_token: string;
      },
      { data: B2BOAuthDiscoveryAuthenticateResponse }
    >(links.auth.session, {
      organization_id,
      member_id,
      intermediate_session_token,
    });

    return response.data;
  } catch (error) {
    throw error as TServerErrorResponse;
  }
};

export const getSession = async (): Promise<Member> => {
  try {
    const response = await authHttp.get<{ member: Member }>(links.auth.session);
    return response.data.member;
  } catch (error) {
    throw error as AxiosError;
  }
};

export const sessionLogout = async () => {
  try {
    const response = await authHttp.post<void>(links.auth.logout);
    return response.data;
  } catch (error) {
    throw error as TServerErrorResponse;
  }
};

export const discoveryMagicLink = async ({ token }: { token: string }) => {
  try {
    const response = await authHttp.get<{
      status: boolean;
      data: B2BMagicLinksDiscoveryAuthenticateResponse;
    }>(links.auth.discovery.magicLinks, {
      params: { token },
    });
    return response.data;
  } catch (error) {
    throw error as TServerErrorResponse;
  }
};

export const getRecoveryCodes = async (
  params: RecoveryCodesRequest,
): Promise<RecoveryCodesResponse> => {
  const response = await authHttp.post<RecoveryCodesResponse>(
    links.auth.recoveryCodes.index,
    params,
  );
  return response.data;
};

export const authenticateWithRecoveryCode = async (
  params: RecoveryCodeAuthenticateRequest,
): Promise<RecoveryCodeAuthenticateResponse> => {
  const response = await authHttp.post<RecoveryCodeAuthenticateResponse>(
    links.auth.recoveryCodes.authenticate,
    params,
  );
  return response.data;
};

export const deleteMfaTotp = async ({
  member_id,
  organization_id,
}: {
  member_id: string;
  organization_id: string;
}) => {
  try {
    const response = await authHttp.post<void>(links.auth.mfaTotpDelete, {
      member_id,
      organization_id,
    });
    return response.data;
  } catch (error) {
    throw error as TServerErrorResponse;
  }
};

export const updateSAMLConnection = async ({
  organization_id,
  connection_id,
  idp_sso_url,
  idp_entity_id,
  x509_certificate,
  idp_slo_url,
}: {
  organization_id: string;
  connection_id: string;
  idp_sso_url: string;
  idp_entity_id: string;
  x509_certificate: string;
  idp_slo_url?: string;
}) => {
  try {
    const response = await authHttp.put(
      `${links.auth.sso.saml}/${organization_id}/connections/${connection_id}`,
      {
        idp_sso_url,
        idp_entity_id,
        x509_certificate,
        idp_slo_url,
        attribute_mapping: {
          email:
            "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress",
          first_name:
            "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/givenname",
          last_name:
            "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/surname",
          full_name:
            "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name",
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error updating SAML connection:", error);
    throw error;
  }
};

export const ssoRedirectUrl = async ({
  connection_id,
}: {
  connection_id: string;
}): Promise<string> => {
  try {
    const response = await authHttp.get<{ redirect_url: string }>(
      links.auth.sso.redirect,
      {
        params: { connection_id },
      },
    );
    return response.data.redirect_url;
  } catch (error) {
    throw error as TServerErrorResponse;
  }
};
